import { extractErrorMessage } from "../utils/acceptSuggestions";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText, Download, ExternalLink, RefreshCw, ShieldCheck,
  Loader2, ArrowLeft, BookOpen, CheckCircle2,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * AdminGuidebooksPage — single source of truth for every customer-facing
 * HTML guidebook. Visible only at /admin/guidebooks (WebAdmin role=admin).
 *
 * The catalogue is fetched from `/api/admin/guidebooks` so any new HTML
 * dropped into /app/frontend/public/ is auto-listed without code changes.
 * The "Refresh All" button calls `POST /api/admin/guidebooks/refresh`,
 * which re-injects the V22.7 capability-matrix banner across every file
 * so the docs always reflect the running build.
 */

// Curated metadata so the cards have real titles + descriptions instead of
// raw filenames. Any file not in this map falls back to a humanised name.
const META = {
  ResiliencyHUB_Complete_Architectural_Guidebook_V23: { group: "Architecture & Technical", title: "Complete Architectural Guidebook (V23)", desc: "Current V23. End-to-end architecture: PDCA, Inheritance Engine, RBAC, federation, BIA modular package, auto-discovery & AI Accept.", current: true, currentLabel: "V23 · CURRENT" },
  ResiliencyHUB_Complete_Architectural_Guidebook_V22: { group: "Architecture & Technical", title: "Complete Architectural Guidebook", desc: "End-to-end architecture: PDCA, Inheritance Engine, RBAC, federation, BIA modular package.", previousVersion: true },
  ResiliencyHUB_Architecture_Analysis_v2:             { group: "Architecture & Technical", title: "Architecture Analysis (v2)", desc: "Backend module map, scalability levers, deployment topology." },
  ResiliencyHUB_Role_Hierarchy_Architecture:          { group: "Architecture & Technical", title: "Role Hierarchy Architecture", desc: "Every RBAC role, scope, and approval flow on one page." },
  ResiliencyHUB_Technical_Whitepaper_Patent:  { group: "Architecture & Technical", title: "Technical Whitepaper (Patent-grade)", desc: "AI-driven BCM patent narrative + claim diagrams." },
  AD_Federation_Architecture_Advisory:        { group: "Architecture & Technical", title: "AD / Identity Federation Advisory", desc: "Reference architecture for Entra / Workday / SCIM federation." },
  ResiliencyHUB_API_Integration_Developer_Guide_V23: { group: "Architecture & Technical", title: "API Integration & Developer Guide (V23)", desc: "Current V23. Endpoint catalog, auth, webhooks, code samples for IT/QA.", current: true, currentLabel: "V23 · CURRENT" },
  API_Integration_Developer_Guide_V22:        { group: "Architecture & Technical", title: "API Integration & Developer Guide", desc: "Endpoint catalog, auth, webhooks, code samples for IT/QA.", previousVersion: true },
  ResiliencyHUB_BIA_EndToEnd_Guidebook_V23:   { group: "Process & Reference", title: "BIA End-to-End Guidebook (V23)", desc: "Current V23. Customer-facing walkthrough of the full Business Impact Analysis journey — configuration, assessment, dependencies, outcomes and reporting.", current: true, currentLabel: "V23 · CURRENT" },
  IT_Function_Manager_Technical_BIA_Runbook:  { group: "Architecture & Technical", title: "IT Function Manager — Technical BIA Runbook (V22.9)", desc: "How to record BIAs for backbone infrastructure & enterprise apps (Core Networking, AD, ERP backbone). Same 5x4 matrix, same cascade, fires BOTH BCP and IT-DRP tasks on approval. Pre-seeded demo: IT-001/IT-002/IT-003.", pinned: true, pinnedLabel: "V22.9 NEW" },
  Resilience_Heatmap_Audit_Evidence_Runbook:  { group: "Architecture & Technical", title: "Resilience Heatmap — Audit Evidence Runbook (V22.9)", desc: "Single-screen Department × RTO-Band view of every approved BIA (Business + Technical). Drill-down to BIA list. Print-to-PDF for the audit binder. ISO 22301 §8.2.2 / SOC 2 evidence. Located: Performance & Compliance Center → Resilience Heatmap tab.", pinned: true, pinnedLabel: "V22.9 NEW · Auditor View" },
  Universal_Asset_Profile_Gate_Runbook:       { group: "Architecture & Technical", title: "Universal Asset Profile Gate — Runbook (V22.9.5)", desc: "Mandatory first-login questionnaire for Process Managers, App Managers, IT Function Managers and contributors. Polymorphic asset profile (Business Process / Application / IT Service / Facility / Vendor). Same StepDialog TurboTax UX. Auto-feeds the BIA Wizard via the credit-efficient AssetPicker.", pinned: true, pinnedLabel: "V22.9.5 NEW · First-Login Gate" },
  Data_Federation_AI_Guidance_Runbook:        { group: "Architecture & Technical", title: "Cross-Module Data Federation & AI Guidance Layer — Runbook (V22.9.6)", desc: "One Profile Gate submission now fans out into assets, vendors, stakeholders, documents, notification chains and recovery tasks — idempotent on source_profile_id. AI Copilot side-panel surfaces deterministic RTO/RPO suggestions on every step + a one-time GPT-4o criticality reasoning on the final step, with every LLM hint hash-protected in audit_log for ISO 22301 §7.5.", pinned: true, pinnedLabel: "V22.9.6 NEW · SSoT + AI Copilot" },
  Asset_Demography_V22_9_8_Runbook:           { group: "Architecture & Technical", title: "Asset Demography Expansion — Runbook (V22.9.8)", desc: "The Profile Gate now captures full Asset Demography per the standard template: 5-step App profile (Identity, Ownership, Hosting, Network Architecture, Connectivity), 2-step Process profile (Scope, Compliance), IT Function with Classification + Physicality. Multi-role merged wizard, deterministic glossary, vendor_connectivity_links federation into BIA dependency dropdown, and a non-blocking yellow enrichment banner for V22.9.5 users.", pinned: true, pinnedLabel: "V22.9.8 NEW · Asset Demography" },
  Public_to_Private_Conversion_V22_9_9_Runbook: { group: "Customer Onboarding & Trial", title: "Public-to-Private Conversion Protocol — Runbook (V22.9.9)", desc: "Retires the public 14-day Free Trial in favour of an Admin-driven demo approval workflow. Public form at /request-demo, business-email gate (hybrid frozenset + DB-blocklist + regex), 7-day demo_observer JWT, gated Meridian Demo Sandbox, manual-mode welcome email until SendGrid is activated.", pinned: true, pinnedLabel: "V22.9.9 NEW · Gated Demo" },

  ResiliencyHUB_Customer_Onboarding_Playbook: { group: "Customer Onboarding & Trial", title: "Customer Onboarding Playbook (V22.9)", desc: "End-to-end onboarding: Admin Panel provisioning, BCM Manager handoff, V22.9 hierarchical invitation cascade. Step-by-step, UI-aligned." },
  ResiliencyHUB_Enterprise_Trial_Blueprint_V23: { group: "Customer Onboarding & Trial", title: "Enterprise Evaluation & Trial Blueprint (V23)", desc: "Current V23. The admin-provisioned Request-Access evaluation model end to end: onboarding, PDCA modules, auto-discovery, AI Accept, guided workflow, federation, feature matrix and conversion.", current: true, currentLabel: "V23 · CURRENT" },
  ResiliencyHUB_Enterprise_Trial_Blueprint:   { group: "Customer Onboarding & Trial", title: "Enterprise Trial Blueprint (Request-Access)", desc: "BCM-Manager-approved discretionary trials. Replaces the retired public trial.", previousVersion: true },
  Federation_Activation_Runbook:              { group: "Customer Onboarding & Trial", title: "Federation Activation Runbook (CS + Customer · Authoritative)", desc: "Single source of truth for federation activation. CS click-by-click on Emergent · backend restart impact · per-source troubleshooting · SCIM walk-through · Demo / Production sync · sign-off checklist." },
  DivIHN_Meeting_Brief:                       { group: "Customer Onboarding & Trial", title: "DivIHN Kickoff Meeting Brief", desc: "Meeting-ready summary for the DivIHN onboarding kickoff. Prepared Mon · 11 May 2026  →  meeting Tue · 12 May 2026. 5-step flow, two-column needs/provides, UAT exit criteria, 10 alignment questions, deep-links to every relevant guidebook.", pinned: true, pinnedLabel: "Meeting · Tue 12 May 2026" },
  DivIHN_EndToEnd_Onboarding_Journey:         { group: "Customer Onboarding & Trial", title: "DivIHN End-to-End Onboarding Journey (Step-by-Step)", desc: "The complete 6-phase customer journey: pre-onboarding inputs to collect → tenant provisioning → federation activation → 30-day UAT → exit criteria → post-UAT decision point on the domain migration. Use this BEFORE deciding on the migration build.", pinned: true, pinnedLabel: "Step-by-Step · Read First" },
  DivIHN_Domain_Migration_Proposal:           { group: "Customer Onboarding & Trial", title: "DivIHN Domain Migration Proposal (.co → .com)", desc: "Safest automated path for the post-UAT domain flip. Two-phase migration with dual-email grace period, snapshot backup, 30-day auto-rollback, and federation re-validation. Awaiting your sign-off before build kicks off (~4 hr).", pinned: true, pinnedLabel: "Proposal · Awaiting Sign-Off" },
  DivIHN_PreMigration_Questionnaire:          { group: "Customer Onboarding & Trial", title: "DivIHN Pre-Migration Questionnaire (Send to their IT Lead)", desc: "10 questions to ask DivIHN's IT / BCM lead AFTER the Migrate Domain feature ships, BEFORE you click the button. Covers email mapping, MX cutover timing, Entra tenant identity, AD bind DN, federation re-issue, rollback authority, communication plan, and audit evidence.", pinned: true, pinnedLabel: "Send Post-Build · Pre-Cutover" },
  DivIHN_Onboarding_Playbook:                 { group: "Customer Onboarding & Trial", title: "DivIHN Onboarding Playbook (Customer-Specific)", desc: "Worked example — intake, federation keys, welcome email." },
  DivIHN_Federation_Request_Package:          { group: "Customer Onboarding & Trial", title: "DivIHN Federation Request Package (to DivIHN Infra Team)", desc: "Per-system questionnaires for M365, AD, ServiceNow, Workday — designed to be sent directly to the customer's Infrastructure Lead." },
  Federation_Cover_Email_Template:            { group: "Customer Onboarding & Trial", title: "Federation Request — Cover Email (Reusable)", desc: "Reusable copy-paste cover email that accompanies the Federation Request Package. Works for DivIHN and any new onboarding customer — replace placeholders." },
  DivIHN_Federation_Implementation_Guide:     { group: "Customer Onboarding & Trial", title: "DivIHN Federation Implementation Guide (Non-Technical)", desc: "Step-by-step: where to paste each value inside ResiliencyHUB Admin after DivIHN responds. Troubleshooting included." },

  DivIHN_UAT_Workflow:                        { group: "UAT & Demo", title: "DivIHN UAT Workflow (94 cases)", desc: "Logic-First UAT for the named DivIHN engagement." },
  UAT_Customer_Onboarding_Guide_V22:          { group: "UAT & Demo", title: "UAT Customer Onboarding Guide", desc: "Generic UAT onboarding script for any customer." },
  UAT_Workflow_Based:                         { group: "UAT & Demo", title: "UAT — Workflow-Based", desc: "Tests grouped by end-to-end business workflow." },
  UAT_Scenario_Based:                         { group: "UAT & Demo", title: "UAT — Scenario-Based", desc: "Tests grouped by real-world disruption scenarios." },
  UAT_Test_Cases:                             { group: "UAT & Demo", title: "UAT — Test Cases", desc: "Atomic test cases catalog (regression-suite style)." },
  ResiliencyHUB_Demo_Walkthrough_V22:         { group: "UAT & Demo", title: "Live Demo Walkthrough", desc: "Click-by-click tour of the Meridian sandbox at 80% maturity." },

  Go_Live_Deployment_Playbook:                { group: "Deployment & Operations", title: "Go-Live Deployment Playbook", desc: "Cut-over runbook — pre-flight, switch, post-validate." },
  Production_Deployment_Strategy:             { group: "Deployment & Operations", title: "Production Deployment Strategy", desc: "Environments, blue/green, rollback, audit shipper config." },
  ResiliencyHUB_P3_Implementation_Roadmap:            { group: "Deployment & Operations", title: "P3 Implementation Roadmap", desc: "Phased plan with milestones / dependencies / owners." },
  ResiliencyHUB_Data_Integration_Dashboard_Mockup:    { group: "Deployment & Operations", title: "Data Integration Dashboard (Mockup)", desc: "Visual reference for the federation-status dashboard." },
  AWS_AI_Continuity_Guidebook:                { group: "Deployment & Operations", title: "AWS Production · AI Continuity Guidebook", desc: "Keep every AI module working after AWS cutover. Emergent-key → OpenAI/Anthropic/Gemini swap, Secrets Manager, VPC egress, IAM, cost controls, Bedrock alternative." },

  Integrated_BIA_Wizard_V3:                   { group: "Process & Reference", title: "Integrated BIA Wizard (v3)", desc: "5-step BIA reference with inheritance worked example." },
  Resource_Gap_Recovery_Plan:                 { group: "Process & Reference", title: "Resource-Gap Recovery Plan", desc: "Sample plan covering staff / supplier / facility gaps." },
  Global_Dependency_Matrix:                   { group: "Process & Reference", title: "Global Dependency Matrix", desc: "Cross-process dependency reference." },
  Asset_Criticality_Audit:                    { group: "Process & Reference", title: "Asset Criticality Audit", desc: "Audit-grade criticality validation report." },
  MockUp_ManualWorkarounds_SOP:               { group: "Process & Reference", title: "Manual Workarounds SOP", desc: "Off-system fallback procedures by process family." },
  Automated_Notification_Log:                 { group: "Process & Reference", title: "Automated Notification Log (sample)", desc: "What good notification telemetry looks like." },
};

const GROUP_ORDER = [
  "Architecture & Technical",
  "Customer Onboarding & Trial",
  "UAT & Demo",
  "Deployment & Operations",
  "Process & Reference",
  "Other",
];

function humanise(name) {
  return name.replace(/_/g, " ").replace(/\.html$/, "").replace(/\bV\d+\b/g, "").trim();
}

function formatBytes(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function AdminGuidebooksPage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [data, setData] = useState({ files: [], last_refresh: {} });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchCatalogue = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/guidebooks");
      setData(res.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to load guidebook catalogue"));
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const res = await api.post("/admin/guidebooks/refresh");
      toast.success(`Refreshed ${res.data.refreshed}/${res.data.total} guidebooks`);
      await fetchCatalogue();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Refresh failed"));
    } finally {
      setRefreshing(false);
    }
  };

  // Guidebook files live behind the authenticated WebAdmin endpoint now, so a
  // plain <a download> can't fetch them. Pull the file with the bearer token
  // and trigger a client-side download from the blob.
  const downloadFile = async (name) => {
    try {
      const res = await api.get(`/admin/guidebooks/file/${encodeURIComponent(name)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Download failed — admin access required"));
    }
  };

  useEffect(() => { fetchCatalogue(); }, []);

  const grouped = {};
  for (const f of data.files || []) {
    const stem = f.name.replace(/\.html$/, "");
    // V22.9 rebrand fallback — if a legacy GBRMS_* file briefly survives a
    // production deploy (additive file-sync), re-key it to the new
    // ResiliencyHUB_* META entry so the card lands in the correct group
    // with the proper title instead of falling through to "OTHER".
    const aliasedStem = stem.startsWith("GBRMS_")
      ? stem.replace(/^GBRMS_/, "ResiliencyHUB_")
      : stem;
    const meta = META[aliasedStem] || META[stem] || { group: "Other", title: humanise(f.name), desc: null };
    const g = meta.group;
    if (!grouped[g]) grouped[g] = [];
    grouped[g].push({ ...f, ...meta });
  }

  // Within each group, surface current (V23) cards first, then pinned
  // (time-sensitive / featured) cards, then everything else alphabetically.
  for (const g of Object.keys(grouped)) {
    grouped[g].sort((a, b) => {
      const ca = a.current ? 1 : 0, cb = b.current ? 1 : 0;
      if (cb !== ca) return cb - ca;
      const pa = a.pinned ? 1 : 0, pb = b.pinned ? 1 : 0;
      if (pb !== pa) return pb - pa;
      return (a.title || a.name).localeCompare(b.title || b.name);
    });
  }

  const last = data.last_refresh || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50 p-4 sm:p-8" data-testid="admin-guidebooks-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <button
              onClick={() => navigate("/admin")}
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-2"
              data-testid="back-to-admin"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Admin Dashboard
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <BookOpen className="w-7 h-7 text-sky-700" />
              Guidebook Library
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-3xl">
              Single, always-current source of every guidebook &amp; blueprint. These
              HTML docs are WebAdmin-only — served through an authenticated endpoint,
              never as public static files. V23 editions are marked <em>Current</em>;
              superseded editions are marked <em>Previous Version</em>. The freshness
              banner is auto-injected on every backend startup and on demand via the
              <em>Refresh All</em> button, so the docs never drift from the running build.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[0.7rem]">
              <ShieldCheck className="w-3 h-3 mr-1" /> WebAdmin only
            </Badge>
            <Button
              onClick={refreshAll}
              disabled={refreshing || loading}
              className="bg-sky-700 hover:bg-sky-800 text-white"
              data-testid="refresh-all-guidebooks"
            >
              {refreshing
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Refreshing…</>
                : <><RefreshCw className="w-4 h-4 mr-2" /> Refresh All</>}
            </Button>
          </div>
        </div>

        {/* Status strip */}
        <Card className="mb-5 border-sky-200 bg-gradient-to-r from-sky-50 to-white">
          <CardContent className="p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="font-semibold text-slate-700">Banner version:</span>
              <code className="bg-white border border-slate-200 rounded px-1.5 py-0.5 text-xs">{last.version || "—"}</code>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-700">Last refreshed:</span>
              <span className="text-slate-600">{last.refreshed_at || "—"}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-700">Coverage:</span>
              <span className="text-slate-600">{last.refreshed || 0} / {last.total || data.files?.length || 0} files</span>
            </div>
          </CardContent>
        </Card>

        {/* Library */}
        {loading ? (
          <div className="text-center py-16 text-slate-500">
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
            Loading guidebook catalogue…
          </div>
        ) : (
          GROUP_ORDER.filter(g => grouped[g]?.length).map(group => (
            <div key={group} className="mb-6" data-testid={`group-${group.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{group}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {grouped[group].map(f => (
                  <Card
                    key={f.name}
                    className={
                      f.current
                        ? "border-emerald-400 ring-2 ring-emerald-200 hover:shadow-md transition-all relative"
                        : f.pinned
                          ? "border-amber-400 ring-2 ring-amber-200 hover:shadow-md transition-all relative"
                          : f.previousVersion
                            ? "border-slate-200 bg-slate-50/60 hover:shadow-sm transition-all relative"
                            : "hover:border-sky-300 hover:shadow-md transition-all"
                    }
                    data-testid={
                      f.current ? `current-card-${f.name}`
                        : f.previousVersion ? `previous-card-${f.name}`
                        : f.pinned ? `pinned-card-${f.name}` : undefined
                    }
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-2.5">
                        <FileText className={`w-5 h-5 flex-shrink-0 mt-0.5 ${f.current ? "text-emerald-600" : f.previousVersion ? "text-slate-400" : f.pinned ? "text-amber-600" : "text-sky-600"}`} />
                        <div className="flex-1 min-w-0">
                          {f.current && (
                            <span className="inline-block bg-emerald-100 text-emerald-800 border border-emerald-300 text-[0.62rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-1.5" data-testid={`badge-current-${f.name}`}>
                              {f.currentLabel || "Current"}
                            </span>
                          )}
                          {f.previousVersion && (
                            <span className="inline-block bg-slate-200 text-slate-600 border border-slate-300 text-[0.62rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-1.5" data-testid={`badge-previous-${f.name}`}>
                              Previous Version
                            </span>
                          )}
                          {f.pinned && !f.current && !f.previousVersion && (
                            <span className="inline-block bg-amber-100 text-amber-900 border border-amber-300 text-[0.62rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-1.5">
                              {f.pinnedLabel || "Pinned"}
                            </span>
                          )}
                          <p className={`text-sm font-bold leading-snug ${f.previousVersion ? "text-slate-500" : "text-slate-800"}`} title={f.title}>
                            {f.title}
                          </p>
                          {f.desc && (
                            <p className="text-xs text-slate-500 leading-snug mt-1 line-clamp-2">{f.desc}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2 text-[0.65rem] text-slate-400">
                            <code className="bg-slate-50 border border-slate-200 rounded px-1 py-0.5">{f.name}</code>
                            <span>{formatBytes(f.size_bytes)}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-3">
                            <a
                              href={`/admin/guidebook-viewer/${encodeURIComponent(f.name)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[0.7rem] font-semibold text-sky-700 bg-sky-50 hover:bg-sky-100 transition-colors"
                              data-testid={`preview-${f.name}`}
                            >
                              <ExternalLink className="w-3 h-3" /> Preview
                            </a>
                            <button
                              type="button"
                              onClick={() => downloadFile(f.name)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[0.7rem] font-semibold text-white bg-sky-700 hover:bg-sky-800 transition-colors"
                              data-testid={`download-${f.name}`}
                            >
                              <Download className="w-3 h-3" /> Download
                            </button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
